import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useStageMovementReport } from '../../../hooks/pipelineTracker/useStageMovementReport';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { CARD, InfoBanner, WarnState, TH } from './trackerUi';

/** Whole observed intervals only: never assign a crossing to an invented event date. */
const StageMovementPanel: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { result, loading, error } = useStageMovementReport(companyId);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const pairs = result?.status === 'ok' ? result.pairs : [];
  const dates = [...new Set(pairs.flatMap((p) => [p.from, p.to]))].sort();
  const start = dates.includes(from) ? from : dates[0] ?? '';
  const end = dates.includes(to) ? to : dates[dates.length - 1] ?? '';
  const invalid = start >= end;
  const selected = invalid ? [] : pairs.filter((p) => p.from >= start && p.to <= end);
  const dateLabel = (date: string) => new Date(`${date}T00:00:00Z`).toLocaleDateString('en-AU', { timeZone: 'UTC' });

  return (
    // Collapsed by default (founder, 29 Sep 2026): it is the week-by-week detail behind the charts
    // above it, so it opens on request rather than pushing the charts down the page.
    <details className={`${CARD} group p-4`} aria-label="Observed stage milestones">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-base font-semibold text-gray-100">
        <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-90" />
        Observed stage milestones
        {pairs.length > 0 && (
          <span className="text-xs font-normal text-gray-400">
            {pairs.length} weekly {pairs.length === 1 ? 'interval' : 'intervals'}, click to open
          </span>
        )}
      </summary>
      <div className="mt-3 space-y-3">
      <InfoBanner>
        Company-wide crossings between published Performance snapshots, using mapped stages for
        residential and commercial deals. Dates show the observation interval, not the exact
        event date. Moves between uploads can be missed. This does not measure first-time-right.
      </InfoBanner>
      {loading ? <SkeletonTable rows={3} columns={5} /> : error ? <WarnState>{error}</WarnState> :
        !result || result.status === 'insufficient_history' || pairs.length === 0 ? (
          <p className="text-sm text-gray-400">At least two Performance snapshots are needed to observe stage milestones.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-3">
              <label className="text-sm text-gray-300">From snapshot
                <select className="ml-2 rounded border border-gray-600 bg-gray-900 p-2" value={start} onChange={(e) => setFrom(e.target.value)}>
                  {dates.map((d) => <option key={d} value={d}>{dateLabel(d)}</option>)}
                </select>
              </label>
              <label className="text-sm text-gray-300">To snapshot
                <select className="ml-2 rounded border border-gray-600 bg-gray-900 p-2" value={end} onChange={(e) => setTo(e.target.value)}>
                  {dates.map((d) => <option key={d} value={d}>{dateLabel(d)}</option>)}
                </select>
              </label>
            </div>
            {invalid ? <WarnState>Choose an end snapshot after the start snapshot.</WarnState> : (
              <div className={`${CARD} overflow-x-auto`}>
                <table className="w-full text-left text-sm text-gray-300">
                  <thead><tr><TH>Observed between</TH><TH right>Reached lender</TH><TH right>Entered settled stage</TH><TH right>Entered lost stage</TH><TH>Coverage</TH></tr></thead>
                  <tbody className="divide-y divide-gray-700">
                    {[...selected].reverse().map((p) => (
                      <tr key={`${p.from}_${p.to}`}>
                        <td className="px-4 py-3">{dateLabel(p.from)} – {dateLabel(p.to)}<div className="text-xs text-gray-400">{p.gapDays} days</div></td>
                        <td className="px-4 py-3 text-right tabular-nums">{p.measurable ? p.reachedLender.count : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{p.measurable ? p.settled.count : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{p.measurable ? p.lost.count : '—'}</td>
                        <td className="px-4 py-3 text-xs">
                          {p.measurable ? `${p.carriedDeals.toLocaleString('en-AU')} shared deals` : 'Not measurable: deal references do not align'}
                          <div>{p.unresolvableDeals} unmapped; {p.arrivedPastLender.count} first seen already past lender; {p.disappeared.count} no longer in export</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-400">
              Reached lender counts a crossing into a lender milestone, not every later move or
              resubmission within lender stages. Lost-stage crossings exclude deals that remain
              in an active stage with a lost status. Disappearing from an export is not treated as a loss.
            </p>
          </>
        )}
    </div>
    </details>
  );
};
export default StageMovementPanel;
